import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import './RollCallInterface.css';

const RollCallInterface = ({ onBack, selectedClass, selectedDate, selectedTime }) => {
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (selectedClass && selectedDate) {
      fetchClassStudents();
      fetchExistingAttendance();
    }
  }, [selectedClass, selectedDate]);

  const fetchClassStudents = async () => {
    try {
      setLoading(true);
      
      // Fetch students for the selected class
      const { data: studentsData, error } = await supabase
        .from('saas_students')
        .select('student_id, name, student_code, class_display, class_no, class_id')
        .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3')
        .eq('class_id', selectedClass)
        .order('class_no');

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      setStudents(studentsData || []);
      
      // Initialize attendance records for each student
      const initialRecords = {};
      studentsData.forEach(student => {
        initialRecords[student.student_id] = 'present'; // Default to present
      });
      setAttendanceRecords(initialRecords);
      
    } catch (error) {
      console.error('Error in fetchClassStudents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingAttendance = async () => {
    try {
      // Check if attendance already exists for this class and date
      const { data: existingRecords, error } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3')
        .eq('class_id', selectedClass)
        .eq('attendance_date', selectedDate);

      if (error) {
        console.error('Error fetching existing attendance:', error);
        return;
      }

      if (existingRecords && existingRecords.length > 0) {
        // Load existing attendance data
        const existingAttendance = {};
        existingRecords.forEach(record => {
          existingAttendance[record.student_id] = record.attendance_status;
        });
        setAttendanceRecords(existingAttendance);
      }
      
    } catch (error) {
      console.error('Error in fetchExistingAttendance:', error);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleStatusIndicatorClick = (studentId) => {
    const currentStatus = attendanceRecords[studentId];
    let newStatus;
    switch (currentStatus) {
      case 'present':
        newStatus = 'absent';
        break;
      case 'absent':
        newStatus = 'late';
        break;
      case 'late':
        newStatus = 'early_leave';
        break;
      case 'early_leave':
        newStatus = 'out';
        break;
      case 'out':
        newStatus = 'present';
        break;
      default:
        newStatus = 'present';
    }
    handleAttendanceChange(studentId, newStatus);
  };

  const getAttendanceSummary = () => {
    const counts = {
      present: 0,
      absent: 0,
      late: 0,
      early_leave: 0,
      out: 0
    };

    Object.values(attendanceRecords).forEach(status => {
      if (counts.hasOwnProperty(status)) {
        counts[status]++;
      }
    });

    return counts;
  };

  const saveAttendanceRecords = async () => {
    try {
      console.log('Starting to save attendance records...');
      setSaving(true);
      
      // Validate required data
      if (!selectedClass || !selectedDate || Object.keys(attendanceRecords).length === 0) {
        alert('缺少必要資料，無法儲存點名記錄');
        return;
      }

      console.log('Selected class:', selectedClass);
      console.log('Selected date:', selectedDate);
      console.log('Attendance records:', attendanceRecords);
      
      const recordsToSave = Object.entries(attendanceRecords).map(([studentId, status]) => ({
        tenant_id: 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
        class_id: selectedClass,
        student_id: studentId,
        attendance_date: selectedDate,
        attendance_status: status,
        notes: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      console.log('Records to save:', recordsToSave);

      // First, delete existing records for this class and date
      console.log('Deleting existing records...');
      const { error: deleteError } = await supabase
        .from('attendance_records')
        .delete()
        .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3')
        .eq('class_id', selectedClass)
        .eq('attendance_date', selectedDate);

      if (deleteError) {
        console.error('Error deleting existing records:', deleteError);
        alert(`刪除舊記錄時發生錯誤: ${deleteError.message}`);
        return;
      }

      console.log('Existing records deleted successfully');

      // Insert new attendance records
      console.log('Inserting new records...');
      const { data: insertedData, error: insertError } = await supabase
        .from('attendance_records')
        .insert(recordsToSave)
        .select();

      if (insertError) {
        console.error('Error saving attendance records:', insertError);
        alert(`儲存記錄時發生錯誤: ${insertError.message}`);
        return;
      }

      console.log('Records saved successfully:', insertedData);
      alert('點名記錄已成功儲存！');
      
      // Wait a moment before navigating back to allow user to see the success message
      setTimeout(() => {
        onBack();
      }, 1000);
      
    } catch (error) {
      console.error('Error in saveAttendanceRecords:', error);
      alert(`儲存失敗: ${error.message || '未知錯誤'}，請重試`);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      present: '#28a745',
      absent: '#dc3545',
      late: '#fd7e14',
      early_leave: '#6f42c1',
      out: '#17a2b8'
    };
    return colors[status] || '#6c757d';
  };

  const getStatusText = (status) => {
    const texts = {
      present: '出席',
      absent: '缺席',
      late: '遲到',
      early_leave: '早退',
      out: '外出'
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <div className="roll-call-interface">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  const summary = getAttendanceSummary();

  return (
    <div className="roll-call-interface">
      {/* Header */}
      <div className="roll-call-header">
        <button className="back-button" onClick={onBack}>
          ← 返回
        </button>
        <h1 className="roll-call-title">點名詳情</h1>
        <div className="class-info">
          <div className="class-name">{selectedClass}</div>
          <div className="class-date">{selectedDate} {selectedTime}</div>
          <div className="class-count">{students.length} 人</div>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <span>檢視模式:</span>
        <div className="toggle-buttons">
          <button
            className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            網格
          </button>
          <button
            className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            列表
          </button>
        </div>
      </div>

      {/* Attendance Summary */}
      <div className="attendance-summary">
        <div className="summary-card present">
          <div className="summary-number">{summary.present}</div>
          <div className="summary-label">出席</div>
        </div>
        <div className="summary-card absent">
          <div className="summary-number">{summary.absent}</div>
          <div className="summary-label">缺席</div>
        </div>
        <div className="summary-card late">
          <div className="summary-number">{summary.late}</div>
          <div className="summary-label">遲到</div>
        </div>
        <div className="summary-card early-leave">
          <div className="summary-number">{summary.early_leave}</div>
          <div className="summary-label">早退</div>
        </div>
        <div className="summary-card out">
          <div className="summary-number">{summary.out}</div>
          <div className="summary-label">外出</div>
        </div>
      </div>

      {/* Student Grid/List */}
      <div className={`student-container ${viewMode}`}>
        {students.map((student) => (
          <div key={student.student_id} className="student-card">
            <div 
              className="student-avatar"
              style={{ backgroundColor: getStatusColor(attendanceRecords[student.student_id] || 'present') }}
            >
              <span className="student-initial">
                {student.name ? student.name.charAt(0) : '?'}
              </span>
              <div 
                className="status-indicator"
                style={{ backgroundColor: getStatusColor(attendanceRecords[student.student_id] || 'present') }}
                onClick={() => handleStatusIndicatorClick(student.student_id)}
                title="點擊更改出勤狀態"
              >
                {getStatusText(attendanceRecords[student.student_id] || 'present').charAt(0)}
              </div>
            </div>
            <div className="student-info">
              <div className="student-name">{student.name}</div>
              <div className="student-id">
                {student.class_display && student.class_no ? 
                  `${student.class_display}${student.class_no.toString().padStart(2, '0')}` : 
                  (student.student_code || 'N/A')}
              </div>
              <div className="student-status">
                {getStatusText(attendanceRecords[student.student_id] || 'present')}
              </div>
            </div>
            <div className="attendance-controls">
              <select
                value={attendanceRecords[student.student_id] || 'present'}
                onChange={(e) => handleAttendanceChange(student.student_id, e.target.value)}
                className="status-select"
              >
                <option value="present">出席</option>
                <option value="absent">缺席</option>
                <option value="late">遲到</option>
                <option value="early_leave">早退</option>
                <option value="out">外出</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="save-section">
        <button 
          className="save-button"
          onClick={saveAttendanceRecords}
          disabled={saving}
        >
          {saving ? '儲存中...' : '儲存點名記錄'}
        </button>

      </div>
    </div>
  );
};

export default RollCallInterface;
